import type { FormEvent, ReactNode } from 'react';
import type { OtpChannel } from '@/domains/auth/types/auth.types';
import { maskPhone } from '@/domains/auth/utils/phone';
import InputMobile from '@/domains/mobile-web/components/InputMobile/InputMobile';
import Button from '@/shared/components/ui/Button';

export const OTP_LENGTH = 6;

interface OtpVerificationCardProps {
  icon: ReactNode;
  title: string;
  // E.164; shown masked
  phone: string;
  channel: OtpChannel;
  code: string;
  onCodeChange: (code: string) => void;
  onSubmit: () => void;
  onResend: () => void;
  resendIn: number;
  error?: string;
  isSending?: boolean;
  isSubmitting?: boolean;
  canSubmit?: boolean;
  // Shown under the card content (the Turnstile widget host)
  children?: ReactNode;
}

/** Card of the "Verifikasi" screens: the code sent by WhatsApp (or SMS) to `phone`. */
const OtpVerificationCard = ({
  icon,
  title,
  phone,
  channel,
  code,
  onCodeChange,
  onSubmit,
  onResend,
  resendIn,
  error,
  isSending = false,
  isSubmitting = false,
  canSubmit = true,
  children,
}: OtpVerificationCardProps) => {
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (code.length === OTP_LENGTH && canSubmit && !isSubmitting) onSubmit();
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="shadowCard h-fit p-6 flex-col flex gap-6 rounded-[24px] mt-[90px] sm:m-auto md:m-auto lg:m-auto mb-[50px]"
    >
      <div className="flex flex-col items-center gap-2 text-center">
        {icon}
        <p className="title2medium text-black">{title}</p>
        <p className="reg12 text-text-grey">
          Masukkan kode verifikasi yang telah kami kirim melalui{' '}
          {channel === 'sms' ? 'SMS' : 'Whatsapp'} ke nomor HP Anda.
        </p>
        <p className="title1bold text-black">{maskPhone(phone)}</p>
      </div>

      <InputMobile
        label="Kode Verifikasi (OTP)"
        name="otp"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={OTP_LENGTH}
        placeholder={`Masukkan ${OTP_LENGTH} digit kode OTP`}
        value={code}
        onChange={(event) =>
          onCodeChange(event.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))
        }
        error={error}
      />

      <div className="flex flex-col items-center gap-1 text-center">
        <p className="reg12 text-text-grey">Belum menerima kode?</p>
        {resendIn > 0 ? (
          <p className="title3bold text-black">Kirim ulang kode dalam {resendIn}s</p>
        ) : (
          <button
            type="button"
            onClick={onResend}
            disabled={isSending}
            className="title3bold text-text-blue-100 disabled:opacity-50"
          >
            {isSending ? 'Mengirim kode...' : 'Kirim ulang kode'}
          </button>
        )}
      </div>

      <Button
        type="submit"
        customButton="btn-fill-blue"
        disabled={code.length !== OTP_LENGTH || !canSubmit || isSubmitting}
      >
        <span className="title2medium text-white">
          {isSubmitting ? 'Memproses...' : 'Verifikasi'}
        </span>
      </Button>

      {children}
    </form>
  );
};

export default OtpVerificationCard;
